import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { useState } from 'react';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import Button from 'primitives/Button';
import { Dialog, DialogSubTitle } from 'primitives/Dialog';
import { DialogPanel } from 'primitives/Dialog';
import { DialogTitle } from 'primitives/Dialog';
import LoadingIndicator from 'primitives/LoadingIndicator';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import { generatePassword } from 'utils/auth';

import VaultActionSlideOver from './VaultActionSlideOver';

const DOCUMENT_VAULT_ACCOUNTS_QUERY = gql(`
    query DocumentVaultAccounts($id: ID!) {
        documentVaultAccounts(investmentEntityId: $id) {
            id
            email
            status
            createdAt
            expiresAt
            investmentEntity {
                id
                name
            }
            emailStatus
        }
    }
  `);

const ADD_DOCUMENT_VAULT_ACCOUNT_MUTATION = gql(`
  mutation CreateDocumentVaultAccount($email: String!, $password: String!, $investmentEntityId: ID!) {
    createDocumentVaultAccount(email: $email, password: $password, investmentEntityId: $investmentEntityId) {
      id
    }
  }
`);

const DocumentVaultAccounts = ({ investmentEntityId }: { investmentEntityId: string }) => {
  const [open, setOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submittedPassword, setSubmittedPassword] = useState('');
  const [documentVaultAccountId, setDocumentVaultAccountId] = useState('');
  const [accountEmail, setAccountEmail] = useState('');
  const defaultPassword = generatePassword(16);
  const [success, setSuccess] = useState(false);
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const {
    showToast: showCopyToast,
    handleToastOpen: handleCopyToastOpen,
    handleToastClose: handleCopyToastClose,
  } = useToast();
  const {
    showToast: showDeactivateVaultAccountToast,
    handleToastOpen: handleDeactivateVaultAccountToastOpen,
    handleToastClose: handleDeactivateVaultAccountToastClose,
  } = useToast();

  const [
    addDocumentVaultAccount,
    { loading: addDocumentVaultAccountLoading, error: addDocumentVaultAccountError },
  ] = useMutation(ADD_DOCUMENT_VAULT_ACCOUNT_MUTATION);
  const { loading, error, data, refetch } = useQuery(DOCUMENT_VAULT_ACCOUNTS_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorMessage error={error} refetch={refetch} />;

  const documentVaultAccounts = data?.documentVaultAccounts;

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-semibold mb-2">Vault</h1>
        <Button onClick={() => setOpen(true)}>Invite Your Accountant</Button>
      </div>

      <DataTable
        data={documentVaultAccounts || []}
        columns={[
          {
            label: 'Email',
            fieldName: 'email',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Investment Entity',
            fieldName: 'investmentEntity.name',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATE',
          },
          {
            label: 'Expires At',
            fieldName: 'expiresAt',
            type: 'DATE',
          },
          {
            label: 'Email Status',
            fieldName: 'emailStatus',
            type: 'EMAIL_STATUS',
          },
        ]}
        emptyListTitle="You haven't added any vault accounts yet."
        onClick={data => {
          setActionOpen(true);
          setDocumentVaultAccountId(data.id);
          setAccountEmail(data.email);
        }}
      />

      <VaultActionSlideOver
        open={actionOpen}
        onClose={(isRevoked?: boolean) => {
          setActionOpen(false);
          if (isRevoked) {
            refetch();
            handleDeactivateVaultAccountToastOpen();
          }
        }}
        documentVaultAccountId={documentVaultAccountId}
        accountEmail={accountEmail}
      />

      <Dialog open={success} onClose={() => setSuccess(false)}>
        <DialogPanel>
          <DialogTitle>Vault Account Created</DialogTitle>
          <p className="text-sm text-gray-500">
            We have sent an email with instructions to the access the vault.
          </p>
          <div className="mt-4">
            <div className="rounded-md bg-gray-50 p-4">
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 text-sm text-gray-900">{email}</div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <div className="relative flex flex-grow items-stretch focus-within:z-10">
                    <input
                      type="text"
                      readOnly
                      value={submittedPassword}
                      className="block w-full rounded-none rounded-l-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(submittedPassword);
                      handleCopyToastOpen();
                    }}
                    className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Please save this password securely. You won't be able to access it again after
                    closing this window.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogPanel>
          <DialogTitle>Invite Your Accountant</DialogTitle>
          <DialogSubTitle>
            Share access to your documents safely and securely through email invites.
          </DialogSubTitle>
          <FormPanel
            loading={addDocumentVaultAccountLoading}
            error={addDocumentVaultAccountError}
            onCancel={() => setOpen(false)}
            onSubmit={data => {
              setEmail(data.email);
              setSubmittedPassword(data.password);
              addDocumentVaultAccount({
                variables: {
                  email: data.email,
                  password: data.password,
                  investmentEntityId: investmentEntityId,
                },
              }).then(() => {
                setSuccess(true);
                setOpen(false);
                handleToastOpen();
                refetch();
              });
            }}
            submitButtonLabel="Invite"
          >
            <FormInput
              fullWidth
              type="text"
              fieldName="email"
              label="Email *"
              defaultValue=""
              validators={{
                required: true,
                maxLength: 255,
                minLength: 3,
              }}
            />
            <FormInput
              fullWidth
              type="text"
              fieldName="password"
              label="Password *"
              defaultValue={defaultPassword}
            />
          </FormPanel>
          <p className="text-sm text-gray-500 mt-4">
            We will send an email with instructions to log in to the vault.
          </p>
        </DialogPanel>
      </Dialog>

      <Toast
        type="success"
        title="Vault Account Created"
        message={'We have sent an email with instructions to the access the vault.'}
        show={showToast}
        handleClose={handleToastClose}
      />
      <Toast
        type="success"
        title="Password Copied"
        message={'The password has been copied to your clipboard.'}
        show={showCopyToast}
        handleClose={handleCopyToastClose}
      />
      <Toast
        type="success"
        title="Account Revoked"
        message="The account has been revoked successfully."
        show={showDeactivateVaultAccountToast}
        handleClose={handleDeactivateVaultAccountToastClose}
      />
    </div>
  );
};

export default DocumentVaultAccounts;
